/**
 * Sistema de Lazy Loading Optimizado para PublikaYA
 * Mejora el rendimiento cargando imágenes solo cuando son necesarias
 */

class LazyImageLoader {
    constructor() {
        this.imageObserver = null;
        this.loadedImages = new Set();
        this.init();
    }

    init() {
        // Verificar soporte para Intersection Observer
        if ('IntersectionObserver' in window) {
            this.setupIntersectionObserver();
        } else {
            // Fallback para navegadores antiguos
            this.loadAllImages();
        }

        // Observar imágenes existentes
        this.observeExistingImages();
        
        // Observar nuevas imágenes que se agreguen dinámicamente
        this.setupMutationObserver();
    }

    setupIntersectionObserver() {
        const options = {
            root: null,
            rootMargin: '50px', // Cargar imágenes 50px antes de que sean visibles
            threshold: 0.01
        };

        this.imageObserver = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    this.loadImage(entry.target);
                    this.imageObserver.unobserve(entry.target);
                }
            });
        }, options);
    }

    observeExistingImages() {
        // Observar imágenes con data-src (lazy loading)
        const lazyImages = document.querySelectorAll('img[data-src]');
        lazyImages.forEach(img => {
            if (this.imageObserver) {
                this.imageObserver.observe(img);
            } else {
                this.loadImage(img);
            }
        });

        // Observar imágenes dinámicas de negocios y categorías
        const dynamicContainers = document.querySelectorAll(
            '.categories-grid, .featured-businesses, .testimonials, #modal-body'
        );
        
        dynamicContainers.forEach(container => {
            if (this.imageObserver) {
                this.imageObserver.observe(container);
            }
        });
    }

    setupMutationObserver() {
        // Observar cambios en el DOM para nuevas imágenes
        const mutationObserver = new MutationObserver((mutations) => {
            mutations.forEach(mutation => {
                mutation.addedNodes.forEach(node => {
                    if (node.nodeType === 1) { // Element node
                        // Buscar imágenes en el nodo agregado
                        const images = node.querySelectorAll ? 
                            node.querySelectorAll('img[data-src], img[src*="logo"], img[src*="banner"]') : 
                            [];
                        
                        images.forEach(img => {
                            this.optimizeImage(img);
                        });

                        // Si el nodo mismo es una imagen
                        if (node.tagName === 'IMG') {
                            this.optimizeImage(node);
                        }
                    }
                });
            });
        });

        mutationObserver.observe(document.body, {
            childList: true,
            subtree: true
        });
    }

    loadImage(img) {
        if (this.loadedImages.has(img)) return;

        const src = img.dataset.src || img.src;
        if (!src) return;

        // Crear una nueva imagen para precargar
        const imageLoader = new Image();
        
        imageLoader.addEventListener('load', () => {
            // Aplicar la imagen cargada
            img.src = src;
            img.classList.add('lazy-loaded');
            
            // Remover data-src si existe
            if (img.dataset.src) {
                delete img.dataset.src;
            }
            
            this.loadedImages.add(img);
            
            // Disparar evento personalizado
            img.dispatchEvent(new CustomEvent('lazyLoaded', {
                detail: { src: src }
            }));
        }, { once: true });

        imageLoader.addEventListener('error', () => {
            // Manejar error de carga
            img.classList.add('lazy-error');
            console.warn('Error cargando imagen lazy:', src);
        }, { once: true });

        imageLoader.src = src;
    }

    optimizeImage(img) {
        // Agregar atributos de optimización si no los tiene
        if (!img.hasAttribute('loading')) {
            img.setAttribute('loading', 'lazy');
        }
        
        if (!img.hasAttribute('decoding')) {
            img.setAttribute('decoding', 'async');
        }

        // Agregar dimensiones si no las tiene (evita layout shift)
        if (!img.hasAttribute('width') && !img.hasAttribute('height')) {
            // Dimensiones por defecto basadas en el contexto
            if (img.closest('.testimonials')) {
                img.setAttribute('width', '80');
                img.setAttribute('height', '80');
            } else if (img.closest('.category-card')) {
                img.setAttribute('width', '64');
                img.setAttribute('height', '64');
            } else if (img.closest('#modal-body')) {
                img.setAttribute('width', '32');
                img.setAttribute('height', '32');
            }
        }

        // Agregar alt text si no lo tiene
        if (!img.hasAttribute('alt')) {
            const altText = this.generateAltText(img);
            if (altText) {
                img.setAttribute('alt', altText);
            }
        }

        // Observar la imagen si tiene data-src
        if (img.dataset.src && this.imageObserver) {
            this.imageObserver.observe(img);
        }
    }

    generateAltText(img) {
        // Generar alt text basado en el contexto
        const src = img.src || img.dataset.src || '';
        
        if (src.includes('logo')) {
            const businessName = img.closest('[data-name]')?.dataset.name || 
                               img.nextElementSibling?.textContent || 
                               'negocio';
            return `Logo de ${businessName}`;
        }
        
        if (src.includes('banner')) {
            return 'Banner promocional';
        }
        
        if (src.includes('user')) {
            const userName = img.closest('.user')?.querySelector('.user-name')?.textContent || 'usuario';
            return `Foto de perfil de ${userName}`;
        }
        
        return 'Imagen';
    }

    loadAllImages() {
        // Fallback: cargar todas las imágenes inmediatamente
        const lazyImages = document.querySelectorAll('img[data-src]');
        lazyImages.forEach(img => this.loadImage(img));
    }

    // Método público para cargar imagen específica
    forceLoad(img) {
        this.loadImage(img);
    }

    // Método público para optimizar imágenes dinámicas
    optimizeDynamicImages(container) {
        const images = container.querySelectorAll('img');
        images.forEach(img => this.optimizeImage(img));
    }
}

// Inicializar el sistema de lazy loading
const lazyLoader = new LazyImageLoader();

// Exportar para uso global
window.LazyImageLoader = lazyLoader;

// CSS para animaciones de carga
const lazyStyles = `
<style>
img[data-src] {
    opacity: 0;
    transition: opacity 0.3s ease-in-out;
}

img.lazy-loaded {
    opacity: 1;
}

img.lazy-error {
    opacity: 0.5;
    filter: grayscale(100%);
}

/* Placeholder mientras carga */
img[data-src]::before {
    content: '';
    display: block;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200% 100%;
    animation: loading 1.5s infinite;
}

@keyframes loading {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
}
</style>
`;

// Inyectar estilos
if (!document.querySelector('#lazy-loading-styles')) {
    const styleElement = document.createElement('style');
    styleElement.id = 'lazy-loading-styles';
    styleElement.textContent = lazyStyles;
    document.head.appendChild(styleElement);
}

console.log('✅ Sistema de Lazy Loading inicializado correctamente');
